import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../api';
import { Search, Globe, Link, Loader2, AlertCircle, CheckCircle, Server, Shield, Lock, X, Trash2, RotateCcw } from 'lucide-react';

const INPUT_CLS = 'w-full bg-gray-950 border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors';

const Scanner = () => {
  const [scanType, setScanType] = useState('ip');
  const [target, setTarget] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [scanHistory, setScanHistory] = useState([]);
  const [currentScanId, setCurrentScanId] = useState(null);
  const pollRef = useRef(null);

  const handleScan = async (e) => {
    e.preventDefault();
    if (!target.trim()) return;

    setScanning(true);
    setError('');
    setResult(null);

    try {
      let endpoint, payload;
      if (scanType === 'ip') {
        endpoint = '/api/scan/ip';
        payload = { network: target, profile: 'quick' };
      } else if (scanType === 'domain') {
        endpoint = '/api/scan/domain';
        payload = { domain: target };
      } else {
        endpoint = '/api/scan/url';
        payload = { url: target };
      }

      const res = await apiClient.post(endpoint, payload);
      const scanId = res.data.scan_id;
      setCurrentScanId(scanId);

      let pollCount = 0;
      const poll = async () => {
        try {
          const detailRes = await apiClient.get(`/api/scan/${scanId}`);

          if (detailRes.data.status === 'completed') {
            setResult(detailRes.data);
            setScanning(false);
            setCurrentScanId(null);
            fetchHistory();
          } else if (detailRes.data.status === 'failed') {
            setError(detailRes.data.error_message || 'Scan failed');
            setScanning(false);
            setCurrentScanId(null);
          } else if (detailRes.data.status === 'cancelled') {
            setError('Scan was cancelled');
            setScanning(false);
            setCurrentScanId(null);
          } else if (pollCount < 60) {
            pollCount++;
            pollRef.current = setTimeout(poll, 2000);
          } else {
            setError('Scan timed out');
            setScanning(false);
            setCurrentScanId(null);
          }
        } catch (err) {
          pollCount++;
          if (pollCount < 60) pollRef.current = setTimeout(poll, 2000);
          else { setScanning(false); setCurrentScanId(null); setError('Failed to get scan results'); }
        }
      };
      pollRef.current = setTimeout(poll, 2000);
    } catch (err) {
      setScanning(false);
      setCurrentScanId(null);
      setError(err.response?.data?.detail || 'Scan request failed');
    }
  };

  const handleCancel = async () => {
    if (!currentScanId) return;
    try {
      await apiClient.post(`/api/scan/${currentScanId}/cancel`);
      if (pollRef.current) clearTimeout(pollRef.current);
      setScanning(false);
      setCurrentScanId(null);
      setError('Scan cancelled');
    } catch {
      setError('Failed to cancel scan');
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await apiClient.get('/api/scan/scans?limit=10');
      setScanHistory(res.data.scans || []);
    } catch {
      setScanHistory([]);
    }
  };

  const handleDeleteScan = async (scanId, e) => {
    e.stopPropagation();
    try {
      await apiClient.delete(`/api/scan/${scanId}`);
      fetchHistory();
    } catch (err) {
      console.error('Delete scan failed:', err.response?.status, err.response?.data);
      alert(`Failed to delete scan: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleRerun = (scan) => {
    setScanType(scan.scan_type);
    setTarget(scan.target);
    setResult(null);
    setError('');
  };

  useEffect(() => { fetchHistory(); }, []);

  const renderResult = () => {
    if (!result || !result.result_data) return null;
    const d = result.result_data;

    if (result.scan_type === 'ip') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-400 mb-1">Hosts Found</h4>
              <div className="text-2xl font-bold text-white">{d.hosts_found || 0}</div>
            </div>
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-400 mb-1">Hosts Up</h4>
              <div className="text-2xl font-bold text-green-400">{d.hosts_up || 0}</div>
            </div>
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-400 mb-1">Open Ports</h4>
              <div className="text-2xl font-bold text-amber-400">{d.total_open_ports || 0}</div>
            </div>
          </div>
          {d.discovered_assets && d.discovered_assets.length > 0 && (
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-400 mb-2">Discovered Assets</h4>
              <div className="space-y-2">
                {d.discovered_assets.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-gray-950/50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Server className="w-4 h-4 text-cyan-400" />
                      <span className="text-white font-mono">{a.ip}</span>
                      {a.hostname && <span className="text-gray-500">({a.hostname})</span>}
                    </div>
                    <span className="text-xs bg-cyan-500/10 text-cyan-400 px-2 py-0.5 rounded border border-cyan-500/20">{a.open_ports} open</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (result.scan_type === 'domain') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-400 mb-2">DNS Resolution</h4>
              <div className="space-y-1">
                {(d.resolved_ips || []).map((ip, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-white">
                    <Server className="w-4 h-4 text-cyan-400" /> {ip}
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-400 mb-2">SSL Certificate</h4>
              {d.ssl_cert ? (
                <div className="space-y-1 text-sm">
                  <div className="text-white"><Lock className="w-4 h-4 inline mr-2 text-green-400" />{d.ssl_cert.issuer}</div>
                  <div className="text-gray-400">Valid until: {d.ssl_cert.not_after}</div>
                </div>
              ) : (
                <div className="text-gray-500 text-sm">No SSL certificate found</div>
              )}
            </div>
          </div>
          {d.technologies && d.technologies.length > 0 && (
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-400 mb-2">Detected Technologies</h4>
              <div className="flex flex-wrap gap-2">
                {d.technologies.map((t, i) => (
                  <span key={i} className="px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded text-xs border border-cyan-500/20">{t}</span>
                ))}
              </div>
            </div>
          )}
          {d.detected_services && d.detected_services.length > 0 && (
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-400 mb-2">Detected Services</h4>
              <div className="space-y-1">
                {d.detected_services.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-white">
                    <Shield className="w-4 h-4 text-amber-400" />
                    {s.name} {s.version && <span className="text-gray-500">v{s.version}</span>}
                    {s.port && <span className="text-gray-500">:{s.port}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (result.scan_type === 'url') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-400 mb-2">Response</h4>
              <div className="text-2xl font-bold text-white">{d.status_code || 'N/A'}</div>
            </div>
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-400 mb-2">Server</h4>
              <div className="text-white">{d.server_fingerprint || 'Unknown'}</div>
            </div>
          </div>
          {d.technologies && d.technologies.length > 0 && (
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-400 mb-2">Technologies</h4>
              <div className="flex flex-wrap gap-2">
                {d.technologies.map((t, i) => (
                  <span key={i} className="px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded text-xs border border-cyan-500/20">{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
        <pre className="text-xs text-gray-300 overflow-auto max-h-96">{JSON.stringify(d, null, 2)}</pre>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-6">Asset Scanner</h1>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
        <div className="flex gap-2 mb-4">
          {[
            { id: 'ip', label: 'IP Address', icon: Search },
            { id: 'domain', label: 'Domain', icon: Globe },
            { id: 'url', label: 'URL', icon: Link },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setScanType(tab.id); setResult(null); setError(''); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                scanType === tab.id ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleScan} className="flex gap-3">
          <input
            type="text"
            value={target}
            onChange={e => setTarget(e.target.value)}
            placeholder={
              scanType === 'ip' ? 'Enter IP address (e.g., 192.168.1.1)' :
              scanType === 'domain' ? 'Enter domain (e.g., example.com)' :
              'Enter URL (e.g., https://example.com)'
            }
            className={INPUT_CLS}
            disabled={scanning}
          />
          {scanning ? (
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2.5 bg-red-500 hover:bg-red-400 text-white rounded-lg font-semibold text-sm transition-colors flex items-center gap-2"
            >
              <X className="w-4 h-4" /> Cancel
            </button>
          ) : (
            <button
              type="submit"
              disabled={!target.trim()}
              className="px-6 py-2.5 bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-700 disabled:text-gray-500 text-gray-950 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2"
            >
              <Search className="w-4 h-4" /> Scan
            </button>
          )}
        </form>

        {error && (
          <div className="mt-4 flex items-center gap-2 text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}
      </div>

      {result && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <h2 className="text-lg font-semibold text-white">Scan Results: {result.target}</h2>
          </div>
          {renderResult()}
        </div>
      )}

      {scanHistory.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Scans</h2>
          <div className="space-y-2">
            {scanHistory.map(s => (
              <div
                key={s.id}
                className="flex items-center justify-between bg-gray-950/50 border border-gray-800 rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-800/50 transition-colors"
                onClick={() => handleRerun(s)}
              >
                <div className="flex items-center gap-3">
                  <RotateCcw className="w-3.5 h-3.5 text-gray-600" />
                  <span className={`w-2 h-2 rounded-full ${
                    s.status === 'completed' ? 'bg-green-400' : s.status === 'failed' ? 'bg-red-400' : 'bg-yellow-400 animate-pulse'
                  }`} />
                  <span className="text-sm text-white">{s.target}</span>
                  <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded">{s.scan_type}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">{new Date(s.started_at).toLocaleString()}</span>
                  <button
                    onClick={(e) => handleDeleteScan(s.id, e)}
                    className="p-1 text-gray-600 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Scanner;
